import React from 'react';
import PropTypes from 'prop-types';
import {inject, observer} from 'mobx-react';
import {action, autorun, computed, observable, reaction} from 'mobx';
import {Link, locationShape, routerShape, withRouter} from 'react-router';

import { BOOKING_BASE_URL } from 'utils/constants';
import autobind from 'autobind-decorator';

import DocumentTitle from 'spa/Components/DocumentTitle';
import DocumentMeta from 'spa/Components/DocumentMeta';
import SignupForm from 'spa/Components/Session/SignupForm';
import LoginForm from 'spa/Components/Session/LoginForm';
import ReferralTherapistCard from 'spa/Components/ReferralTherapistCard';
import NotFound from 'spa/Pages/NotFound';
import PageLoading, {
  PageLoadingTitle,
  PageLoadingSubtitle,
  PageLoadingLine,
} from 'spa/Components/PageLoading';

import {cn} from 'spa/css';

import {withConfig, withI18n} from 'spa/Components/Context';

@withRouter
@inject(({
  store, store: {
    userReferral, notifications, redeemReferral, tracker,
  }, params,
}) => ({
  store,
  userReferral,
  notifications,
  redeemReferral,
  tracker,
  params,
  landing: userReferral.getOne(params.code),
}))
@observer
@withConfig
@withI18n
class InviteContent extends React.Component {
  static propTypes = {
    store: PropTypes.object.isRequired,
    userReferral: PropTypes.object.isRequired,
    redeemReferral: PropTypes.object.isRequired,
    params: PropTypes.object.isRequired,
    name: PropTypes.string,
    location: locationShape.isRequired,
    router: routerShape.isRequired,
    notifications: PropTypes.object.isRequired,
    tracker: PropTypes.shape({
      track: PropTypes.func.isRequired,
    }).isRequired,
  }

  UNSAFE_componentWillMount() {
    const {config, i18n} = this.context;

    this.props.userReferral.maybeLoadOne(this.props.params.code);
    this.activeOffers = config.getValue('b2c.active-referral-offer-codes', i18n.umRegion);
    this.referralValue = config.getValue('b2c.referral-friend-gets-value', i18n.umRegion);
  }

  componentDidMount() {
    const {instance} = this.referralInfo;
    const {tracker} = this.props;

    this.listeners = [
      reaction(
        () => [this.props.store.session.isLoggedIn, this.referralInfo.instance],
        () => {
          if (this.props.store.session.isLoggedIn && this.referralInfo.instance) {
            this.redeemReferralCode();

            tracker.track('Logged in via Referral link', {
              referrerID: instance.referrer_id,
              referrerName: instance.name,
            });
          }
        },
        {
          fireImmediately: true,
          name: 'apply referral code',
        },
      ),

      autorun(() => {
        const {activeOffers} = this;
        const {params} = this.props;
        const {code} = params;

        if (this.referralInfo && activeOffers.indexOf(code) === -1) {
          if (!instance) return null;

          if (instance.suggestedWorker && instance.suggestedWorker.id) {
            tracker.track('Viewed Referral Invite Page', {
              hasBooking: true,
              worker: instance.suggestedWorker.id,
              speciality: instance.suggestedSpeciality.id,
            });
          } else {
            tracker.track('Viewed Referral Invite Page', {
              hasBooking: false,
            });
          }
        }
      }, {
        name: 'tracking view event',
      }),
    ];
  }

  componentWillUnmount() {
    while (this.listeners.length) {
      this.listeners.pop()();
    }
  }

  @autobind
  @action('isActiveOffer - InviteContent')
  isActiveOffer(code) {
    if (!code) return false;
    const {activeOffers} = this;
    let isCode = false;
    let isActive = false;
    let trackTitle = '';

    activeOffers.forEach((offer) => {
      if (code === offer.code) {
        isCode = true;
        if (offer.active) {
          isActive = true;
        }

        ({trackTitle} = offer);
      }
    });

    return {isCode, isActive, trackTitle};
  }

  @computed get referralInfo() {
    setTimeout(() => this.props.userReferral.maybeLoadOne(this.props.params.code), 0);
    return this.props.userReferral.getOne(this.props.params.code);
  }

  redeemReferralCode() {
    const {isActiveOffer, referralValue} = this;
    const {
      notifications, params, redeemReferral, router,
    } = this.props;
    const {code} = params;
    const {i18n} = this.context;

    // [0] = is a promotional referral code [1] = is it active in this region [2] = identifier
    const offerDetails = isActiveOffer(code);

    // If the code is a promotional referral code but not active in this region push them to booking
    if (offerDetails.isCode && !offerDetails.isActive) {
      router.push(`/${i18n.lc}/${BOOKING_BASE_URL}/`);
    } else { // Otherwise lets apply the code whether its a promotional one or normal user referral
      redeemReferral.load({voucher: code}).then((response) => {
        const titlePartial = i18n._x('We\'ve applied your referral discount to your account! Enjoy your next treatment.', 'Invite page applied message');
        const title = Object.keys(titlePartial).map(partial => (
          titlePartial[partial]
        )).join('');

        notifications.add({
          id: 'applied-code-message',
          type: 'success',
          title,
        });

        router.push(`/${i18n.lc}/${BOOKING_BASE_URL}/`);
      }).catch((error) => {
        notifications.add({
          id: 'invalid-code-message',
          type: 'error',
          title: i18n.__error(error),
        });

        router.push(`/${i18n.lc}/${BOOKING_BASE_URL}/`);
      });
    }
  }

  @observable sessionPage = 'signup';

  @autobind @action onClickSignup(event) {
    event.preventDefault();
    this.sessionPage = 'signup';
  }

  @autobind @action onClickLogin(event) {
    event.preventDefault();
    this.sessionPage = 'login';
  }

  @autobind @action onSignup() {
    const {params, tracker} = this.props;
    const {code} = params;
    const {isActiveOffer, referralInfo} = this;
    const {instance} = referralInfo;
    if (!instance) return null;

    // [0] = is a promotional referral code [1] = is it active in this region [2] = identifier
    const offerDetails = isActiveOffer(code);

    // If the code is a promotional referral code but not active in this region push them to booking
    if (offerDetails.isCode && !offerDetails.isActive) {
      return null;
    }

    if (offerDetails.trackTitle) {
      tracker.track(`Signup via ${offerDetails.trackTitle} Referral link`, {});
    }

    return tracker.track('Signup via Referral link', {
      referrerID: instance.referrer_id,
      referrerName: instance.name,
    });
  }

  render() {
    const {i18n} = this.context;
    const {referralInfo} = this;
    const {instance} = referralInfo;

    if (instance && !instance.success) {
      return (
        <NotFound location={this.props.location} />
      );
    }

    if (instance && instance.success && !this.props.store.session.isLoggedIn) {
      const {name} = instance;
      const {isActiveOffer, onSignup, sessionPage} = this;
      const {code} = this.props.params;

      // [0] = is a promotional referral code [1] = is it active in this region [2] = identifier
      const offerDetails = isActiveOffer(code);

      return (
        <div className="invite">
          <DocumentTitle
            title={i18n._x('Invite', 'Document title for referral signup page')}>
            <DocumentMeta
              title={i18n._x('Invite', 'Meta title for referral signup page')}
              description={i18n._x('Redeem your free credit towards bookings at Urban', 'Meta description for referral signup page')}
              meta={{name: {robots: 'noindex'}}}
            />
          </DocumentTitle>
          <section className="invite__top-content">
            <div className="container">
              <div className="row">
                {offerDetails.isCode && !offerDetails.isActive && (
                  <div className="invite__banner">
                    <h1 className="invite__banner-title">{i18n._x('Sorry, this code is not valid in your region.', 'Promo code invalid region')}</h1>
                  </div>
                )}
                {offerDetails.isCode && offerDetails.isActive && offerDetails.trackTitle === 'Facebook' && (
                  <div className="invite__banner">
                    <img
                      className="invite__facebook-image"
                      src="https://res.cloudinary.com/huxr6hrje/image/upload/f_auto,q_60/v1502202914/refer/facebook_2x.png"
                      alt={i18n._x('Facebook logo', 'Facebook referral page')} />
                    <p className="invite__banner-title">Create your free account <br />with<span className="invite__banner-title--bold"> {this.referralValue} off</span> of your first order.</p>
                  </div>
                )}
                {offerDetails.isCode && offerDetails.isActive && offerDetails.trackTitle === 'Plenish' && (
                  <div className="invite__banner">
                    <img
                      className="invite__plenish-image"
                      src="https://res.cloudinary.com/huxr6hrje/image/upload/f_auto,q_60/v1502202914/refer/plenish_2x.png"
                      alt={i18n._x('Plenish logo', 'Plenish referral page')} />
                    <p className="invite__banner-title">Create your free account <br />with<span className="invite__banner-title--bold"> {this.referralValue} off</span> of your first order.</p>
                    <p className="invite__plenish-subtitle">Limited to first 500 accounts</p>
                  </div>
                )}
                <div
                  className={cn(
                    instance.suggestedWorker &&
                    instance.suggestedWorker.name &&
                    'col-lg-7',
                    'invite__content',
                  )}>
                  {referralInfo && !offerDetails.isCode && (
                    <header>
                      <h1 className="invite__title">
                        {i18n._x('Book your first treatment with %(referralValue)s off', 'Referral signup page title', {
                          referralValue: this.referralValue,
                        })}
                      </h1>
                    </header>
                  )}
                  <div>
                    <p className="invite__subtitle">{i18n._x('Urban is an app where you can book massage, beauty, physio / osteo to your home at short notice.', 'Referral signup page subtitle')}</p>
                  </div>
                  {instance.suggestedWorker && instance.suggestedWorker.name && (
                    <ReferralTherapistCard suggestedWorker={instance.suggestedWorker} />
                  )}
                </div>
                <div
                  className={cn(instance.suggestedWorker &&
                    instance.suggestedWorker.name &&
                  'col-lg-4 col-lg-offset-1')}>
                  <div
                    className={cn(
                      (offerDetails.isCode || !instance.suggestedWorker || !instance.suggestedWorker.name) &&
                      'invite__signup-form--center',
                      'invite__signup-form',
                    )}>
                    <div className="invite__signup-links">
                      <div className="row invite__signup-links-box">
                        <a
                          href="#"
                          className={cn(
                            sessionPage === 'signup' &&
                            'invite__top-link--active',
                            'invite__top-link',
                          )}
                          onClick={this.onClickSignup}>
                          {i18n._x('New User?', 'Signup page')}
                        </a>
                        <a
                          href="#"
                          className={cn(
                            sessionPage === 'login' &&
                            'invite__top-link--active',
                            'invite__top-link',
                          )}
                          onClick={this.onClickLogin}>
                          {i18n._x('Already Registered?', 'Login page')}
                        </a>
                      </div>
                    </div>
                    {sessionPage === 'signup' && offerDetails.isCode && (
                      <div className="invite__signup-top">
                        <p className="invite__signup-title">{i18n._x('Sign Up', 'Facebook referral page')}</p>
                        <p className="invite__signup-subtitle">{i18n._x('Create an account to complete your booking.', 'Facebook referral page')}</p>
                      </div>
                    )}
                    <div className="invite__signup-box">
                      {sessionPage === 'login' ? (
                        <LoginForm />
                      ) : (<SignupForm onSignup={onSignup} />
                      )}
                    </div>
                    <p
                      className={cn(
                        sessionPage === 'login' &&
                        'invite__bottom-text--invisible',
                        'invite__bottom-text',
                      )}>
                      {i18n.__react('By creating an account, we will share your email with your referrer. You agree to our %(termsAndConditions)s and %(privacyPolicy)s', {
                        termsAndConditions: <Link className="link" to={`/${i18n.lc}/legal`} target="_blank" rel="noopener noreferrer">{i18n._x('terms and conditions', 'Referral lanfing page signup')}</Link>,
                        privacyPolicy: <Link className="link" to={`/${i18n.lc}/privacy-policy`} target="_blank" rel="noopener noreferrer">{i18n._x('privacy policy', 'Referral lanfing page signup')}</Link>,
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      );
    }

    return (
      <div className="dark v-container">
        <div className="container">
          <PageLoading>
            <PageLoadingTitle />
            <PageLoadingSubtitle className="show-desktop" />
            <PageLoadingLine />
            <PageLoadingLine />
            <PageLoadingLine />
          </PageLoading>
        </div>
      </div>
    );
  }
}

export default InviteContent;



// WEBPACK FOOTER //
// ./spa/Pages/Invite/InviteContent.jsx